import { useTheme } from "@mui/material";
import { useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardDetailFallback } from "web_myplann/Dashboard/Components/DashboardDetailFallback";
import { DashboardDetailHeader } from "web_myplann/Dashboard/Components/DashboardDetailHeader";
import { DashboardDetailStickyHeader } from "web_myplann/Dashboard/Components/DashboardDetailStickyHeader";
import { DashboardDetailV2Content } from "web_myplann/Dashboard/Components/DashboardDetailV2Content";
import { DashboardTotalContent } from "web_myplann/Dashboard/Components/DashboardTotalContent";
import {
	getDashboardIndexByType,
	useDashboardData,
} from "web_myplann/Dashboard/Hooks/useDashboardData";
import { saveDashboardIndex } from "web_myplann/Dashboard/Hooks/useSavedDashboardIndex";
import { PortfolioReportingOverviewTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import { isEnumValue } from "web_myplann/Shared/Utils/parsers";

export const DashboardDetailV2Page = () => {
	const { type } = useParams<{ type: string }>();
	const { palette } = useTheme();
	const navigate = useNavigate();
	const heroRef = useRef<HTMLDivElement>(null);

	const { dashboards, isLoading, isError, refetch } = useDashboardData();

	const activeIndex = isEnumValue(PortfolioReportingOverviewTypeEnum, type)
		? Math.max(getDashboardIndexByType(dashboards, type), 0)
		: 0;
	const dashboard = dashboards[activeIndex];

	if (!dashboard) {
		return (
			<DashboardDetailFallback
				isLoading={isLoading}
				isError={isError}
				onRetry={refetch}
			/>
		);
	}

	const handleDashboardChange = (index: number) => {
		const next = dashboards[index];
		if (!next || next.id === dashboard.id) return;

		saveDashboardIndex(index);
		navigate(Routing.DashboardDetail.path(next.overviewType), {
			replace: true,
		});
	};

	const isTotal =
		dashboard.overviewType === PortfolioReportingOverviewTypeEnum.Total;

	return (
		<MyPlannLayout
			disableContentPadding
			bgColor={palette.surface.muted}
			mainSx={{ display: "flex", flexDirection: "column" }}
		>
			<DashboardDetailStickyHeader heroRef={heroRef} dashboard={dashboard} />
			<DashboardDetailHeader
				heroRef={heroRef}
				dashboard={dashboard}
				dashboardCount={dashboards.length}
				activeIndex={activeIndex}
				onDashboardChange={handleDashboardChange}
			/>
			{isTotal ? (
				<DashboardTotalContent dashboards={dashboards} />
			) : (
				<DashboardDetailV2Content dashboard={dashboard} />
			)}
		</MyPlannLayout>
	);
};
